import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useUnifiedDashboard } from "../contexts/UnifiedDashboardContext";
import LoadingSpinner from "../components/LoadingSpinner";
import { Users, AlertTriangle, Clock, Archive, Calendar, FileText, Download, LayoutGrid, MessageSquare, Unlock, AlertCircle, Shield, Briefcase } from "lucide-react";
import DataTable from "../components/DataTable";
import { engagementDisplayName } from "../utils/engagementName";
import { fetchJSON, API_BASE_URL } from "../utils/api";
import { LateAccountsAnalyzer } from "../utils/excel/analyzers/LateAccountsAnalyzer.js";
import ChartContainer from "../components/ChartContainer";
import ChargeableHoursDetails from "../components/ChargeableHoursDetails";
import ScrollableTabs from "../components/ScrollableTabs";
import OverviewCards from "../components/OverviewCards";
import Complaints from "../components/Complaints";
import UnlockingArchived from "../components/UnlockingArchived";
import EqrLapses from "../components/EqrLapses";
import CyberSecurity from "../components/CyberSecurity";
import AcLapses from "../components/AcLapses";
import HeadcountDetails from "../components/HeadcountDetails";
import SortableHeader, { usePagedSortableData, TablePager } from "../components/SortableTable";
import { exportToExcel } from "../utils/excelExport";
import { NOT_LIVE_QM_TABS, NotLiveBanner } from "../components/shared/NotLive";
import { exportExcelBtnCls } from '../components/shared/ExportExcelButton';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// "2025-01" -> "Jan-25"
const monthLabel = (yyyymm) => {
  if (!yyyymm || !/^\d{4}-\d{2}/.test(yyyymm)) return yyyymm || '';
  const [year, mon] = yyyymm.split('-');
  return `${MONTH_NAMES[parseInt(mon, 10) - 1]}-${year.slice(-2)}`;
};

// Status chips for the three overdue tabs. The API returns every status (Late
// is judged by the threshold alone); which ones the tab shows is decided here,
// independently of the weekly email's own lists on the Overdue Rules page.
// Late Accounts' services come from that page, not from a chip.
const chipCls = (on) => `px-2.5 py-1 rounded-full text-xs border transition-colors ${
  on ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`;
const OverdueFilterBar = ({ groups, shown, total, note }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3">
    {groups.map(g => (
      <div key={g.label} className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-slate-600 mr-1">{g.label}</span>
        {g.options.map(o => (
          <button key={o} type="button" onClick={() => g.onToggle(o)} className={chipCls(g.selected.includes(o))}>{o}</button>
        ))}
        <button type="button" onClick={() => g.onSet(g.options)} className="text-xs text-blue-600 hover:text-blue-800 ml-1">All</button>
        <button type="button" onClick={() => g.onSet(g.defaults)} className="text-xs text-blue-600 hover:text-blue-800">Default</button>
      </div>
    ))}
    <p className="text-xs text-slate-500">
      Showing {shown} of {total} late record(s). Filters only change this view — the weekly email uses its own lists (Management → Overdue Rules).{note && ` ${note}`}
    </p>
  </div>
);

// Lookup values plus anything the data carries that the lookups no longer
// list (a retired status still on old rows) — otherwise those rows could never
// be shown.
const withDataValues = (options, rows, key) => {
  const out = [...options];
  rows.forEach(r => { const v = r[key]; if (v && v !== 'N/A' && !out.includes(v)) out.push(v); });
  return out;
};

const QMDashboard = () => {
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  const [lateAccountsData, setLateAccountsData] = useState(null);
  const [lateArchivalData, setLateArchivalData] = useState(null);
  const [loadingLateAccounts, setLoadingLateAccounts] = useState(false);
  const [loadingLateArchival, setLoadingLateArchival] = useState(false);

  // Engagement status lookups feed the overdue tabs' filter chips. Default
  // selection = the in-progress statuses (lookup flags is_initial_status /
  // is_default). null = "not chosen yet" → falls back to the defaults.
  const [statusLookups, setStatusLookups] = useState([]);
  const [statusFilter, setStatusFilter] = useState({ lateAccounts: null, archival: null, fsSigning: null });
  const statusValues = useMemo(() => statusLookups.map(o => o.value), [statusLookups]);
  const defaultStatuses = useMemo(() => {
    const flagged = statusLookups.filter(o => o.isInitialStatus || o.isDefault).map(o => o.value);
    return flagged.length ? flagged : statusValues;
  }, [statusLookups, statusValues]);
  const setTabStatuses = (tab) => (vals) => setStatusFilter(prev => ({ ...prev, [tab]: vals }));
  const toggleTabStatus = (tab, current) => (v) =>
    setTabStatuses(tab)(current.includes(v) ? current.filter(x => x !== v) : [...current, v]);
  // Deliberately blank (= all records), unlike the L&D filters which default to
  // the last month: a date filter here also drops not-yet-archived records
  // (they have no archival date to fall in range), and those overdue rows are
  // exactly what this tab exists to surface.
  const [lateArchivalDateRange, setLateArchivalDateRange] = useState({ startDate: '', endDate: '' });

  const [lateFsSigningData, setLateFsSigningData] = useState(null);
  const [loadingLateFsSigning, setLoadingLateFsSigning] = useState(false);

  const [timesheetDefaultersData, setTimesheetDefaultersData] = useState(null);
  const [loadingTimesheetDefaulters, setLoadingTimesheetDefaulters] = useState(false);
  const [timesheetMonthRange, setTimesheetMonthRange] = useState({ startMonth: '', endMonth: '' });
  const [showDefaultersOnly, setShowDefaultersOnly] = useState(false);
  const [severityThresholds, setSeverityThresholds] = useState({ high: 6, medium: 3 });

  const [chargeableHoursData, setChargeableHoursData] = useState(null);
  const [loadingChargeableHours, setLoadingChargeableHours] = useState(false);
  const [chargeableMonthRange, setChargeableMonthRange] = useState({ startMonth: '', endMonth: '' });

  // Register stats (Complaints / Unlocking / EQR / Cyber / A&C) — used by the
  // Overview aggregation. The register tabs themselves fetch their own data
  // inside the copied legacy components.
  const [registerStats, setRegisterStats] = useState({
    complaints: null, unlocking: null, eqr: null, cyber: null, ac: null,
  });

  const [headcountAnalysis, setHeadcountAnalysis] = useState(null);
  const [loadingHeadcount, setLoadingHeadcount] = useState(false);

  const { isAuthenticated, hasPermission } = useAuth();
  const { switchDashboard } = useUnifiedDashboard();
  const canExport = hasPermission('qm:view');

  // Set the current dashboard in context
  useEffect(() => {
    switchDashboard("qm");
  }, [switchDashboard]);

  // Lookups for the overdue tabs' filter chips (reads are open to any login).
  useEffect(() => {
    if (!isAuthenticated()) return;
    (async () => {
      try {
        const st = await fetchJSON('/api/settings/lookups/engagement_status?active=true');
        setStatusLookups(st.options || []);
      } catch (e) {
        console.error('❌ Error fetching overdue filter lookups:', e);
      }
    })();
  }, [isAuthenticated]);

  // Late Accounts: status chips (services are already scoped by the API), then
  // the trend analysis and the summary recomputed from what is left (the
  // signed trend follows the same chips so the chart and the table agree).
  const laStatusOptions = useMemo(() => withDataValues(statusValues, lateAccountsData?.lateAccounts || [], 'status'), [statusValues, lateAccountsData]);
  const laStatuses = statusFilter.lateAccounts ?? defaultStatuses;
  const filteredLateAccounts = useMemo(() => {
    if (!lateAccountsData) return null;
    const keep = r => laStatuses.includes(r.status);
    const data = lateAccountsData.lateAccounts.filter(keep);
    const signed = (lateAccountsData.signedAccounts || []).filter(keep);

    const partnerStats = {};
    let totalDaysLate = 0;
    data.forEach(r => {
      // 按合夥人歸戶用「現名優先」：姓名快照是當時的文件，同一人改名後舊案與
      // 新案的字串不同，拿字串當鍵會把一個人算成兩位（utils/staffNow.js）。
      const partner = r.partnerNow || r.partner || 'Unassigned';
      if (!partnerStats[partner]) partnerStats[partner] = { count: 0, totalDays: 0 };
      partnerStats[partner].count++;
      partnerStats[partner].totalDays += r.daysLate || 0;
      totalDaysLate += r.daysLate || 0;
    });
    const totalCount = data.length;
    const avgDaysLate = totalCount > 0 ? Math.round(totalDaysLate / totalCount) : 0;

    // Cumulative timeline via the existing analyzer (per-partner summary rows
    // are its input shape).
    const summaryRecords = Object.entries(partnerStats).map(([partner, stats]) => ({
      id: `summary-${partner}`,
      partner,
      lateAccountsCount: stats.count,
      signedAccountsCount: 0,
      data: { Partner: partner, 'Late Accounts': stats.count, 'Signed Accounts': 0 },
    }));
    const analysis = new LateAccountsAnalyzer().analyze(data, summaryRecords, signed, null, null);

    return { data, summary: { totalCount, avgDaysLate, partnerStats }, analysis };
  }, [lateAccountsData, laStatuses]);

  // Late Archival: status chips + the archival-date range.
  const archStatusOptions = useMemo(() => withDataValues(statusValues, lateArchivalData?.data || [], 'status'), [statusValues, lateArchivalData]);
  const archStatuses = statusFilter.archival ?? defaultStatuses;
  const filteredLateArchival = useMemo(() => {
    if (!lateArchivalData) return null;
    const { startDate, endDate } = lateArchivalDateRange;
    const hasFilter = startDate || endDate;

    const data = lateArchivalData.data.filter(r => {
      if (!archStatuses.includes(r.status)) return false;
      if (!hasFilter) return true;
      // Exclude not-yet-archived records (null archivalDate) when a date filter is active
      if (!r.archivalDate) return false;
      const d = r.archivalDate; // YYYY-MM-DD — lexicographic compare works
      if (startDate && d < startDate) return false;
      if (endDate && d > endDate) return false;
      return true;
    });

    // Recompute summary from filtered set
    const totalCount = data.length;
    const partnerStats = {};
    let totalDaysLate = 0;
    let notArchivedCount = 0;
    data.forEach(r => {
      // 按合夥人歸戶用「現名優先」：姓名快照是當時的文件，同一人改名後舊案與
      // 新案的字串不同，拿字串當鍵會把一個人算成兩位（utils/staffNow.js）。
      const partner = r.partnerNow || r.partner || 'Unassigned';
      if (!partnerStats[partner]) partnerStats[partner] = { count: 0, totalDays: 0 };
      partnerStats[partner].count++;
      partnerStats[partner].totalDays += r.daysLate || 0;
      totalDaysLate += r.daysLate || 0;
      if (!r.archivalDate) notArchivedCount++;
    });
    const avgDaysLate = totalCount > 0 ? Math.round(totalDaysLate / totalCount) : 0;
    return { data, summary: { totalCount, avgDaysLate, notArchivedCount, partnerStats } };
  }, [lateArchivalData, lateArchivalDateRange, archStatuses]);

  // Late FS Signing: status chips.
  const fsStatusOptions = useMemo(() => withDataValues(statusValues, lateFsSigningData?.data || [], 'status'), [statusValues, lateFsSigningData]);
  const fsStatuses = statusFilter.fsSigning ?? defaultStatuses;
  const filteredLateFsSigning = useMemo(() => {
    if (!lateFsSigningData) return null;
    const data = lateFsSigningData.data.filter(r => fsStatuses.includes(r.status));
    const partnerStats = {};
    let totalDaysLate = 0;
    data.forEach(r => {
      // 按合夥人歸戶用「現名優先」：姓名快照是當時的文件，同一人改名後舊案與
      // 新案的字串不同，拿字串當鍵會把一個人算成兩位（utils/staffNow.js）。
      const partner = r.partnerNow || r.partner || 'Unassigned';
      if (!partnerStats[partner]) partnerStats[partner] = { count: 0, totalDays: 0 };
      partnerStats[partner].count++;
      partnerStats[partner].totalDays += r.daysLate || 0;
      totalDaysLate += r.daysLate || 0;
    });
    const totalCount = data.length;
    const avgDaysLate = totalCount > 0 ? Math.round(totalDaysLate / totalCount) : 0;
    return { data, summary: { totalCount, avgDaysLate, partnerStats } };
  }, [lateFsSigningData, fsStatuses]);

  // Fetch Late Accounts (the trend analysis runs in filteredLateAccounts)
  useEffect(() => {
    const fetchLateAccounts = async () => {
      if (!isAuthenticated()) return;

      setLoadingLateAccounts(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/qm/late-accounts`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch late accounts: ${response.statusText}`);
        }

        const result = await response.json();
        if (result.success) {
          setLateAccountsData(result);
          console.log('✅ Late Accounts data loaded:', result.lateAccounts.length, 'late,', result.signedAccounts?.length || 0, 'signed');
        }
      } catch (error) {
        console.error('❌ Error fetching late accounts:', error);
        setError(`Error loading late accounts: ${error.message}`);
      } finally {
        setLoadingLateAccounts(false);
      }
    };

    fetchLateAccounts();
  }, [isAuthenticated]);

  // Fetch Late Archival
  useEffect(() => {
    const fetchLateArchival = async () => {
      if (!isAuthenticated()) return;

      setLoadingLateArchival(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/qm/late-archival`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch late archival: ${response.statusText}`);
        }

        const result = await response.json();
        if (result.success) {
          setLateArchivalData(result);
          console.log('✅ Late Archival data loaded:', result.data.length, 'records');
        }
      } catch (error) {
        console.error('❌ Error fetching late archival:', error);
        setError(`Error loading late archival: ${error.message}`);
      } finally {
        setLoadingLateArchival(false);
      }
    };

    fetchLateArchival();
  }, [isAuthenticated]);

  // Fetch Late FS Signing (engagements past their expected FS signing date)
  useEffect(() => {
    const fetchLateFsSigning = async () => {
      if (!isAuthenticated()) return;

      setLoadingLateFsSigning(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/qm/late-fs-signing`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch late FS signing: ${response.statusText}`);
        }

        const result = await response.json();
        if (result.success) {
          setLateFsSigningData(result);
          console.log('✅ Late FS Signing data loaded:', result.data.length, 'records');
        }
      } catch (error) {
        console.error('❌ Error fetching late FS signing:', error);
        setError(`Error loading late FS signing: ${error.message}`);
      } finally {
        setLoadingLateFsSigning(false);
      }
    };

    fetchLateFsSigning();
  }, [isAuthenticated]);

  // Fetch Timesheet Defaulters
  useEffect(() => {
    const fetchTimesheetDefaulters = async () => {
      if (!isAuthenticated()) return;

      setLoadingTimesheetDefaulters(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/qm/timesheet-defaulters`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch timesheet defaulters: ${response.statusText}`);
        }

        const result = await response.json();
        if (result.success) {
          setTimesheetDefaultersData(result);
        }
      } catch (error) {
        console.error('❌ Error fetching timesheet defaulters:', error);
        setError(`Error loading timesheet defaulters: ${error.message}`);
      } finally {
        setLoadingTimesheetDefaulters(false);
      }
    };

    fetchTimesheetDefaulters();
  }, [isAuthenticated]);

  // Fetch Chargeable Hours (refetches when the month range changes)
  useEffect(() => {
    const fetchChargeableHours = async () => {
      if (!isAuthenticated()) return;

      setLoadingChargeableHours(true);
      try {
        const params = new URLSearchParams();
        if (chargeableMonthRange.startMonth) params.set('startMonth', chargeableMonthRange.startMonth);
        if (chargeableMonthRange.endMonth) params.set('endMonth', chargeableMonthRange.endMonth);

        const response = await fetch(
          `${API_BASE_URL}/api/qm/chargeable-hours?${params}`,
          { method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json' } }
        );

        if (!response.ok) throw new Error(`Failed to fetch chargeable hours: ${response.statusText}`);

        const result = await response.json();
        if (result.success) {
          setChargeableHoursData(result);
        }
      } catch (error) {
        console.error('❌ Error fetching chargeable hours:', error);
        setError(`Error loading chargeable hours: ${error.message}`);
      } finally {
        setLoadingChargeableHours(false);
      }
    };

    fetchChargeableHours();
  }, [isAuthenticated, chargeableMonthRange]);

  // Fetch the five register stats (legacy envelopes) for the Overview aggregation
  useEffect(() => {
    const fetchRegisterStats = async () => {
      if (!isAuthenticated()) return;

      const endpoints = [
        ['complaints', '/api/qm/registers/complaints/stats'],
        ['unlocking', '/api/qm/registers/unlocking/stats'],
        ['eqr', '/api/qm/registers/eqr/stats'],
        ['cyber', '/api/qm/registers/cyber/stats'],
        ['ac', '/api/qm/registers/ac/stats'],
      ];
      try {
        const results = await Promise.all(endpoints.map(async ([key, path]) => {
          const response = await fetch(`${API_BASE_URL}${path}`, {
            method: 'GET',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
          });
          if (!response.ok) throw new Error(`Failed to fetch ${key} stats: ${response.statusText}`);
          const result = await response.json();
          return [key, result.data];
        }));
        setRegisterStats(Object.fromEntries(results));
      } catch (error) {
        console.error('❌ Error fetching register stats:', error);
        setError(`Error loading register stats: ${error.message}`);
      }
    };

    fetchRegisterStats();
  }, [isAuthenticated]);

  // Fetch Headcount analysis (derived server-side from the staff master)
  useEffect(() => {
    const fetchHeadcount = async () => {
      if (!isAuthenticated()) return;

      setLoadingHeadcount(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/qm/headcount`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch headcount: ${response.statusText}`);
        }

        const result = await response.json();
        // Accept either the raw analyze() shape or a {success, data} envelope
        const analysis = result && result.data && result.data.divisionStats
          ? result.data
          : result;
        setHeadcountAnalysis(analysis && analysis.divisionStats ? analysis : null);
      } catch (error) {
        console.error('❌ Error fetching headcount:', error);
        setError(`Error loading headcount: ${error.message}`);
      } finally {
        setLoadingHeadcount(false);
      }
    };

    fetchHeadcount();
  }, [isAuthenticated]);

  // Synthetic rows for HeadcountDetails' division/year dropdowns and empty check
  const headcountRows = useMemo(() => {
    if (!headcountAnalysis?.divisionStats) return [];
    const rows = [];
    headcountAnalysis.divisionStats.forEach(div => {
      Object.keys(div.years || {}).forEach(year => {
        rows.push({ division: div.division, year });
      });
    });
    return rows;
  }, [headcountAnalysis]);

  // Client-side month filter + defaulters-only toggle for timesheet records
  const filteredTimesheets = useMemo(() => {
    if (!timesheetDefaultersData) return [];
    const { startMonth, endMonth } = timesheetMonthRange;
    return timesheetDefaultersData.data.filter(r => {
      if (showDefaultersOnly && !(Number(r.marksDeducted) > 0)) return false;
      if (!r.month) return true;
      const m = String(r.month).slice(0, 7); // YYYY-MM — lexicographic compare works
      if (startMonth && m < startMonth) return false;
      if (endMonth && m > endMonth) return false;
      return true;
    });
  }, [timesheetDefaultersData, timesheetMonthRange, showDefaultersOnly]);

  const timesheetStats = useMemo(() => {
    if (!filteredTimesheets.length) return null;
    const totalRecords = filteredTimesheets.length;
    const uniqueEmployees = new Set(filteredTimesheets.map(r => r.employeeName)).size;
    const marksPerEmployee = {};
    filteredTimesheets.forEach(r => {
      const name = r.employeeName || 'Unknown';
      marksPerEmployee[name] = (marksPerEmployee[name] || 0) + (Number(r.marksDeducted) || 0);
    });
    const topDefaulters = Object.entries(marksPerEmployee)
      .map(([name, marks]) => ({ employeeName: name, totalMarksDeducted: marks }))
      .sort((a, b) => b.totalMarksDeducted - a.totalMarksDeducted);
    const monthlyEmployees = {};
    filteredTimesheets.forEach(r => {
      // Only count employees who actually had marks deducted (i.e. are true defaulters)
      if (!r.month || !(Number(r.marksDeducted) > 0)) return;
      const yyyymm = String(r.month).slice(0, 7); // "2025-01"
      if (!monthlyEmployees[yyyymm]) monthlyEmployees[yyyymm] = new Set();
      monthlyEmployees[yyyymm].add(r.employeeName);
    });
    const monthlyDefaultingTrend = Object.fromEntries(
      Object.keys(monthlyEmployees)
        .sort() // YYYY-MM sorts chronologically
        .map(yyyymm => [monthLabel(yyyymm), monthlyEmployees[yyyymm].size])
    );
    return { totalRecords, uniqueEmployees, topDefaulters, monthlyDefaultingTrend };
  }, [filteredTimesheets]);

  // Defaulters ranked summary rows — hoisted to component scope so the sortable
  // header hook can run unconditionally (hooks can't live inside the JSX IIFE).
  const defaultersRanked = useMemo(() => {
    const totals = {};
    const counts = {};
    filteredTimesheets.forEach(r => {
      const name = r.employeeName || 'Unknown';
      totals[name] = (totals[name] || 0) + (Number(r.marksDeducted) || 0);
      counts[name] = (counts[name] || 0) + 1;
    });
    return Object.entries(totals)
      .map(([name, marks]) => ({ name, marks, count: counts[name] }))
      .sort((a, b) => b.marks - a.marks);
  }, [filteredTimesheets]);
  const {
    rows: sortedDefaulters,
    sortConfig: defaultersSortConfig,
    requestSort: requestDefaultersSort,
    page: defaultersPage,
    setPage: setDefaultersPage,
    total: defaultersTotal,
    pageSize: defaultersPageSize,
  } = usePagedSortableData(defaultersRanked);

  // Overview aggregation — KPIs assembled client-side from the fetched
  // endpoints; the overdue figures follow the tabs' status chips so
  // the Overview never disagrees with the tab it summarises.
  const overviewSummary = useMemo(() => ({
    lateAccountsAnalysis: {
      summaryStats: { totalLateAccounts: filteredLateAccounts?.summary?.totalCount || 0 },
    },
    lateArchivalAnalysis: {
      lateEngagements: filteredLateArchival?.summary?.totalCount || 0,
      partnersInvolved: Object.keys(filteredLateArchival?.summary?.partnerStats || {}).length,
    },
    timesheetAnalysis: {
      uniqueEmployees: timesheetDefaultersData
        ? new Set(timesheetDefaultersData.data.map(r => r.employeeName)).size
        : 0,
    },
    chargeableHoursAnalysis: {
      auditHours: chargeableHoursData?.summary?.auditHours || 0,
      nonAuditHours: chargeableHoursData?.summary?.nonAuditHours || 0,
    },
    registers: {
      complaints: registerStats.complaints?.total || 0,
      unlocking: registerStats.unlocking?.total || 0,
      eqr: registerStats.eqr?.total || 0,
      cyber: registerStats.cyber?.total || 0,
      ac: registerStats.ac?.total || 0,
    },
    headcountAnalysis: headcountAnalysis || {},
  }), [filteredLateAccounts, filteredLateArchival, timesheetDefaultersData, chargeableHoursData, registerStats, headcountAnalysis]);

  // Overview charts data
  const lateAccountsByPartner = useMemo(() => Object.fromEntries(
    Object.entries(filteredLateAccounts?.summary?.partnerStats || {}).map(([partner, stats]) => [partner, stats.count])
  ), [filteredLateAccounts]);

  const avgDaysLateByPartner = useMemo(() => Object.fromEntries(
    Object.entries(filteredLateArchival?.summary?.partnerStats || {}).map(([partner, stats]) => [
      partner,
      stats.count > 0 ? Math.round(stats.totalDays / stats.count) : 0,
    ])
  ), [filteredLateArchival]);

  // Tab bar — 12 tabs, rendered with the ScrollableTabs component.
  // 尚未啟動的 tab 帶 notLive，在 tab bar 上打灰並標 Not live。
  const tabs = [
    { id: 'overview', name: 'Overview', icon: LayoutGrid },
    { id: 'lateAccounts', name: 'Late Accounts', icon: AlertTriangle },
    { id: 'archival', name: 'Late Archival', icon: Archive },
    { id: 'fsSigning', name: 'Late FS Signing', icon: Calendar },
    { id: 'timesheets', name: 'Timesheets', icon: FileText },
    { id: 'chargeableHours', name: 'Chargeable Hours', icon: Clock },
    { id: 'headcount', name: 'Headcount', icon: Users },
    { id: 'complaints', name: 'Complaints', icon: MessageSquare },
    { id: 'unlocking', name: 'Unlocking', icon: Unlock },
    { id: 'eqrLapses', name: 'EQR Lapses', icon: AlertCircle },
    { id: 'cyberSecurity', name: 'Cyber Security', icon: Shield },
    { id: 'acLapses', name: 'A&C Lapses', icon: Briefcase },
  ].map(tab => ({ ...tab, notLive: NOT_LIVE_QM_TABS.includes(tab.id) }));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Error Display */}
      {error && (
        <div className="px-6 pt-6">
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col h-full">
        {/* Secondary Navigation (Tabs) — 12 tabs, scrollable */}
        <div className="shrink-0">
            <ScrollableTabs
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={setActiveTab}
            />
        </div>

        {/* Main Content Area */}
        <main className="flex-1 overflow-auto bg-slate-50 p-6">
            {NOT_LIVE_QM_TABS.includes(activeTab) && <NotLiveBanner className="mb-6" />}
            {activeTab === 'overview' && (
                <div className="space-y-6 animate-fade-in">
                    {(loadingLateAccounts || loadingLateArchival || loadingTimesheetDefaulters) ? (
                        <div className="flex justify-center items-center py-12">
                            <LoadingSpinner />
                            <p className="ml-3 text-slate-600">Loading dashboard data...</p>
                        </div>
                    ) : (
                        <>
                            <OverviewCards
                                data={{ lateAccounts: filteredLateAccounts?.data || [] }}
                                summary={overviewSummary}
                            />

                            {/* Charts Row - Late Accounts & Archival */}
                            {(lateAccountsData || lateArchivalData) && (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {lateAccountsData && (
                                        <ChartContainer
                                            title="Late Accounts by Partner"
                                            data={lateAccountsByPartner}
                                            type="bar"
                                        />
                                    )}
                                    {lateArchivalData && (
                                        <ChartContainer
                                            title="Average Archival Days Late by Partner"
                                            data={avgDaysLateByPartner}
                                            type="bar"
                                        />
                                    )}
                                </div>
                            )}

                            {/* Timesheet Charts */}
                            {timesheetDefaultersData && timesheetStats?.topDefaulters && timesheetStats.topDefaulters.length > 0 && (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    <ChartContainer
                                        title="Top Timesheet Defaulters (Total Marks Deducted)"
                                        data={Object.fromEntries(
                                            timesheetStats.topDefaulters
                                                .slice(0, 10)
                                                .map((d) => [d.employeeName, d.totalMarksDeducted])
                                        )}
                                        type="bar"
                                    />
                                    <ChartContainer
                                        title="Monthly Defaulting Employees Trend"
                                        data={timesheetStats.monthlyDefaultingTrend}
                                        type="line"
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
            {activeTab === 'lateAccounts' && (
                <div className="space-y-6 animate-fade-in">
                    {loadingLateAccounts ? (
                        <div className="flex justify-center items-center py-12">
                            <LoadingSpinner />
                        </div>
                    ) : filteredLateAccounts ? (
                        <>
                            <OverdueFilterBar
                                shown={filteredLateAccounts.data.length}
                                total={lateAccountsData.lateAccounts.length}
                                note="Services covered are set on Management → Overdue Rules."
                                groups={[
                                    { label: 'Status', options: laStatusOptions, selected: laStatuses, defaults: defaultStatuses,
                                      onToggle: toggleTabStatus('lateAccounts', laStatuses), onSet: setTabStatuses('lateAccounts') },
                                ]}
                            />
                            {/* Summary Statistics for Late Accounts */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Late Accounts</p>
                                    <div className="flex items-end justify-between mt-2">
                                        <h3 className="text-2xl font-bold text-slate-900">{filteredLateAccounts.summary.totalCount || 0}</h3>
                                    </div>
                                </div>
                                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Average Days Late</p>
                                    <div className="flex items-end justify-between mt-2">
                                        <h3 className="text-2xl font-bold text-slate-900">{filteredLateAccounts.summary.avgDaysLate || 0}</h3>
                                    </div>
                                </div>
                                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Partners Affected</p>
                                    <div className="flex items-end justify-between mt-2">
                                        <h3 className="text-2xl font-bold text-slate-900">{Object.keys(filteredLateAccounts.summary.partnerStats || {}).length}</h3>
                                    </div>
                                </div>
                            </div>

                            {/* Cumulative Timeline Chart */}
                            {filteredLateAccounts.analysis?.cumulativeTimeline && filteredLateAccounts.analysis.cumulativeTimeline.length > 0 && (
                                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                                    <ChartContainer
                                        title="Late Accounts Cumulative Trend Over Time"
                                        data={filteredLateAccounts.analysis.cumulativeTimeline}
                                        type="multiline"
                                        height={400}
                                    />
                                </div>
                            )}

                            {/* Partner Breakdown Chart */}
                            {filteredLateAccounts.summary.partnerStats && Object.keys(filteredLateAccounts.summary.partnerStats).length > 0 && (
                                <ChartContainer
                                    title="Late Accounts by Partner"
                                    data={Object.fromEntries(
                                        Object.entries(filteredLateAccounts.summary.partnerStats).map(([partner, stats]) => [partner, stats.count])
                                    )}
                                    type="bar"
                                />
                            )}

                            {/* Data Table - Original Format using DataTable component */}
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                <div className="p-6">
                                    <h3 className="text-lg font-medium text-slate-900 mb-4">Client Details</h3>
                                    <DataTable
                                        data={filteredLateAccounts.data.map((acc, idx) => ({
                                            id: `late-${idx}`,
                                            clientName: acc.clientName,
                                            serviceDescription: acc.serviceDescription,
                                            partner: acc.partner,
                                            director: acc.director,
                                            manager: acc.manager,
                                            // 改名提示一起帶進表格（DataTable 的 name cell 印
                                            // 「快照 (now: 現名)」，排序與匯出用現名）。
                                            clientNameNow: acc.clientNameNow,
                                            clientGroupNow: acc.clientGroupNow,
                                            partnerNow: acc.partnerNow,
                                            directorNow: acc.directorNow,
                                            managerNow: acc.managerNow,
                                            clientGroup: acc.clientGroup,
                                            fye: acc.fye,
                                            status: acc.status,
                                            daysLate: acc.daysLate,
                                            isListed: acc.isListed,
                                            data: {
                                                'Client Group': acc.clientGroup || '',
                                                Client: engagementDisplayName(acc.clientName, acc.serviceDescription),
                                                Partner: acc.partner,
                                                Director: acc.director,
                                                Manager: acc.manager,
                                                'Year End': acc.fye,
                                                Listed: acc.isListed ? 'Yes' : 'No',
                                                'Days Late': acc.daysLate,
                                                Status: acc.status
                                            }
                                        }))}
                                        category="lateAccounts"
                                    />
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                            <p className="text-slate-500 text-center">No late accounts data available</p>
                        </div>
                    )}
                </div>
            )}
            {activeTab === 'archival' && (
                <div className="space-y-6 animate-fade-in">
                    {loadingLateArchival ? (
                        <div className="flex justify-center items-center py-12">
                            <LoadingSpinner />
                        </div>
                    ) : filteredLateArchival ? (
                        <>
                            <OverdueFilterBar
                                shown={filteredLateArchival.data.length}
                                total={lateArchivalData.data.length}
                                groups={[
                                    { label: 'Status', options: archStatusOptions, selected: archStatuses, defaults: defaultStatuses,
                                      onToggle: toggleTabStatus('archival', archStatuses), onSet: setTabStatuses('archival') },
                                ]}
                            />
                            {/* Date Range Filter */}
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                              <div className="flex flex-wrap items-center gap-4">
                                <span className="text-sm font-medium text-slate-600">Filter by Archival Date</span>
                                <div className="flex flex-wrap items-center gap-3">
                                  <div className="flex items-center space-x-2">
                                    <label className="text-xs text-slate-500 font-medium">From</label>
                                    <input
                                      type="date"
                                      value={lateArchivalDateRange.startDate}
                                      onChange={e => setLateArchivalDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                                      className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <label className="text-xs text-slate-500 font-medium">To</label>
                                    <input
                                      type="date"
                                      value={lateArchivalDateRange.endDate}
                                      onChange={e => setLateArchivalDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                                      className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                  </div>
                                  {(lateArchivalDateRange.startDate || lateArchivalDateRange.endDate) && (
                                    <button
                                      onClick={() => setLateArchivalDateRange({ startDate: '', endDate: '' })}
                                      className="text-sm text-blue-600 hover:text-blue-800"
                                    >
                                      Clear
                                    </button>
                                  )}
                                </div>
                                {(lateArchivalDateRange.startDate || lateArchivalDateRange.endDate) && (
                                  <span className="text-xs text-slate-500 italic">Note: records not yet archived are hidden when a date filter is active</span>
                                )}
                              </div>
                            </div>
                            {/* Summary Statistics for Late Archival */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Late Archival</p>
                                    <div className="flex items-end justify-between mt-2">
                                        <h3 className="text-2xl font-bold text-slate-900">{filteredLateArchival.summary.totalCount || 0}</h3>
                                    </div>
                                </div>
                                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Average Days Late</p>
                                    <div className="flex items-end justify-between mt-2">
                                        <h3 className="text-2xl font-bold text-slate-900">{filteredLateArchival.summary.avgDaysLate || 0}</h3>
                                    </div>
                                </div>
                                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Not Archived</p>
                                    <div className="flex items-end justify-between mt-2">
                                        <h3 className="text-2xl font-bold text-slate-900">{filteredLateArchival.summary.notArchivedCount || 0}</h3>
                                    </div>
                                </div>
                            </div>

                            {/* Partner Breakdown Chart */}
                            {filteredLateArchival.summary.partnerStats && Object.keys(filteredLateArchival.summary.partnerStats).length > 0 && (
                                <ChartContainer
                                    title="Late Archival by Partner"
                                    data={Object.fromEntries(
                                        Object.entries(filteredLateArchival.summary.partnerStats).map(([partner, stats]) => [partner, stats.count])
                                    )}
                                    type="bar"
                                />
                            )}

                            {/* Data Table */}
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                <div className="p-6">
                                    <h3 className="text-lg font-medium text-slate-900 mb-4">Late Archival Details ({filteredLateArchival.data.length} records)</h3>
                                    <DataTable data={filteredLateArchival.data} category="lateArchival" />
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                            <p className="text-slate-500 text-center">No late archival data available</p>
                        </div>
                    )}
                </div>
            )}
            {activeTab === 'fsSigning' && (
                <div className="space-y-6 animate-fade-in">
                    {loadingLateFsSigning ? (
                        <div className="flex justify-center items-center py-12">
                            <LoadingSpinner />
                        </div>
                    ) : filteredLateFsSigning ? (
                        <>
                            <OverdueFilterBar
                                shown={filteredLateFsSigning.data.length}
                                total={lateFsSigningData.data.length}
                                groups={[
                                    { label: 'Status', options: fsStatusOptions, selected: fsStatuses, defaults: defaultStatuses,
                                      onToggle: toggleTabStatus('fsSigning', fsStatuses), onSet: setTabStatuses('fsSigning') },
                                ]}
                            />
                            {/* Summary Statistics for Late FS Signing */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Late FS Signing</p>
                                    <div className="flex items-end justify-between mt-2">
                                        <h3 className="text-2xl font-bold text-slate-900">{filteredLateFsSigning.summary.totalCount || 0}</h3>
                                    </div>
                                </div>
                                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Average Days Late</p>
                                    <div className="flex items-end justify-between mt-2">
                                        <h3 className="text-2xl font-bold text-slate-900">{filteredLateFsSigning.summary.avgDaysLate || 0}</h3>
                                    </div>
                                </div>
                                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Partners Affected</p>
                                    <div className="flex items-end justify-between mt-2">
                                        <h3 className="text-2xl font-bold text-slate-900">{Object.keys(filteredLateFsSigning.summary.partnerStats || {}).length}</h3>
                                    </div>
                                </div>
                            </div>

                            {/* Partner Breakdown Chart */}
                            {filteredLateFsSigning.summary.partnerStats && Object.keys(filteredLateFsSigning.summary.partnerStats).length > 0 && (
                                <ChartContainer
                                    title="Late FS Signing by Partner"
                                    data={Object.fromEntries(
                                        Object.entries(filteredLateFsSigning.summary.partnerStats).map(([partner, stats]) => [partner, stats.count])
                                    )}
                                    type="bar"
                                />
                            )}

                            {/* Data Table */}
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                <div className="p-6">
                                    <h3 className="text-lg font-medium text-slate-900 mb-4">Late FS Signing Details ({filteredLateFsSigning.data.length} records)</h3>
                                    <p className="text-sm text-slate-500 mb-4">Engagements past their Expected Financial Statements Signing Date (from the QC6/QC7 Conclusion) with no Signed Financial Statements record yet.</p>
                                    <DataTable data={filteredLateFsSigning.data} category="lateFsSigning" />
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                            <p className="text-slate-500 text-center">No late FS signing data available</p>
                        </div>
                    )}
                </div>
            )}
            {activeTab === 'timesheets' && (
                <div className="space-y-6 animate-fade-in">
                    {loadingTimesheetDefaulters ? (
                        <div className="flex justify-center items-center py-12">
                            <LoadingSpinner />
                        </div>
                    ) : timesheetDefaultersData ? (
                        <>
                            {/* Month Range Filter */}
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                              <div className="flex flex-wrap items-center gap-4">
                                <span className="text-sm font-medium text-slate-600">Filter by Month</span>
                                <div className="flex flex-wrap items-center gap-3">
                                  <div className="flex items-center space-x-2">
                                    <label className="text-xs text-slate-500 font-medium">From</label>
                                    <input
                                      type="month"
                                      value={timesheetMonthRange.startMonth}
                                      onChange={e => setTimesheetMonthRange(prev => ({ ...prev, startMonth: e.target.value }))}
                                      className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <label className="text-xs text-slate-500 font-medium">To</label>
                                    <input
                                      type="month"
                                      value={timesheetMonthRange.endMonth}
                                      onChange={e => setTimesheetMonthRange(prev => ({ ...prev, endMonth: e.target.value }))}
                                      className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                  </div>
                                  {(timesheetMonthRange.startMonth || timesheetMonthRange.endMonth) && (
                                    <button
                                      onClick={() => setTimesheetMonthRange({ startMonth: '', endMonth: '' })}
                                      className="text-sm text-blue-600 hover:text-blue-800"
                                    >
                                      Clear
                                    </button>
                                  )}
                                </div>
                                <div className="ml-auto">
                                  <button
                                    onClick={() => setShowDefaultersOnly(v => !v)}
                                    className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                                      showDefaultersOnly
                                        ? 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100'
                                        : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
                                    }`}
                                  >
                                    <AlertTriangle className={`h-4 w-4 ${showDefaultersOnly ? 'text-red-500' : 'text-slate-400'}`} />
                                    <span>{showDefaultersOnly ? 'Showing Defaulters Only' : 'Show Defaulters Only'}</span>
                                    {showDefaultersOnly && (
                                      <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">
                                        {filteredTimesheets.length}
                                      </span>
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                            {/* Summary Statistics */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Records</p>
                                    <div className="flex items-end justify-between mt-2">
                                        <h3 className="text-2xl font-bold text-slate-900">{timesheetStats?.totalRecords ?? 0}</h3>
                                    </div>
                                </div>
                                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Unique Employees</p>
                                    <div className="flex items-end justify-between mt-2">
                                        <h3 className="text-2xl font-bold text-slate-900">{timesheetStats?.uniqueEmployees ?? 0}</h3>
                                    </div>
                                </div>
                                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Top Defaulter Marks</p>
                                    <div className="flex items-end justify-between mt-2">
                                        <h3 className="text-2xl font-bold text-slate-900">
                                            {timesheetStats?.topDefaulters?.[0]?.totalMarksDeducted ?? 0}
                                        </h3>
                                    </div>
                                </div>
                            </div>

                            {/* Charts */}
                            {timesheetStats?.topDefaulters?.length > 0 && (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    <ChartContainer
                                        title="Top Timesheet Defaulters (Total Marks Deducted)"
                                        data={Object.fromEntries(
                                            timesheetStats.topDefaulters
                                                .slice(0, 10)
                                                .map((d) => [d.employeeName, d.totalMarksDeducted])
                                        )}
                                        type="bar"
                                    />
                                    <ChartContainer
                                        title="Monthly Defaulting Employees Trend"
                                        data={timesheetStats.monthlyDefaultingTrend}
                                        type="line"
                                    />
                                </div>
                            )}

                            {/* Data Table */}
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                <div className="p-6">
                                    <h3 className="text-lg font-medium text-slate-900 mb-4">
                                        Timesheet Details ({filteredTimesheets.length} records)
                                    </h3>
                                    {/* Defaulters ranked summary — shown when filter is active */}
                                    {showDefaultersOnly && (() => {
                                      const ranked = defaultersRanked;
                                      const getSeverity = (marks) =>
                                        marks >= severityThresholds.high
                                          ? { label: 'High', cls: 'bg-red-100 text-red-800' }
                                          : marks >= severityThresholds.medium
                                          ? { label: 'Medium', cls: 'bg-orange-100 text-orange-800' }
                                          : { label: 'Low', cls: 'bg-yellow-100 text-yellow-800' };
                                      return (
                                        <div className="mb-6">
                                          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Defaulters — Ranked by Total Marks Deducted</p>
                                            <div className="flex items-center gap-3">
                                              {/* Export button (qm:view — export folds into view) */}
                                              {canExport && (
                                              <button
                                                onClick={() => {
                                                  const rows = ranked.map((row, i) => ({
                                                    'Rank': i + 1,
                                                    'Employee': row.name,
                                                    'Offences': row.count,
                                                    'Total Marks Deducted': row.marks,
                                                    'Severity': getSeverity(row.marks).label,
                                                  }));
                                                  exportToExcel(rows, 'Timesheet_Defaulters_Ranked.xlsx');
                                                }}
                                                className={exportExcelBtnCls}
                                              >
                                                <Download className="w-4 h-4" />
                                                Export Excel
                                              </button>
                                              )}
                                              {/* Threshold controls */}
                                              <div className="flex items-center gap-3 text-xs text-slate-600">
                                                <span className="font-medium">Severity Thresholds:</span>
                                              <label className="flex items-center gap-1">
                                                <span className="inline-flex px-1.5 py-0.5 rounded-full bg-red-100 text-red-800 font-semibold">High</span>
                                                <span>≥</span>
                                                <input
                                                  type="number"
                                                  min={severityThresholds.medium + 1}
                                                  value={severityThresholds.high}
                                                  onChange={e => {
                                                    const val = Math.max(Number(e.target.value), severityThresholds.medium + 1);
                                                    setSeverityThresholds(p => ({ ...p, high: val }));
                                                  }}
                                                  className="w-14 px-1.5 py-0.5 border border-slate-300 rounded text-xs text-center focus:outline-none focus:ring-1 focus:ring-red-400"
                                                />
                                                <span>marks</span>
                                              </label>
                                              <label className="flex items-center gap-1">
                                                <span className="inline-flex px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-800 font-semibold">Medium</span>
                                                <span>≥</span>
                                                <input
                                                  type="number"
                                                  min={1}
                                                  max={severityThresholds.high - 1}
                                                  value={severityThresholds.medium}
                                                  onChange={e => {
                                                    const val = Math.min(Math.max(Number(e.target.value), 1), severityThresholds.high - 1);
                                                    setSeverityThresholds(p => ({ ...p, medium: val }));
                                                  }}
                                                  className="w-14 px-1.5 py-0.5 border border-slate-300 rounded text-xs text-center focus:outline-none focus:ring-1 focus:ring-orange-400"
                                                />
                                                <span>marks</span>
                                              </label>
                                                <button
                                                  onClick={() => setSeverityThresholds({ high: 6, medium: 3 })}
                                                  className="text-slate-500 hover:text-slate-600 underline"
                                                >Reset</button>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="table-scroll">
                                            <table className="w-full text-sm">
                                              <thead className="bg-slate-50">
                                                <tr className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
                                                  <SortableHeader label="#" sortKey="rank" sortable={false} className="px-3 py-2 font-medium w-8" />
                                                  <SortableHeader label="Employee" sortKey="name" sortConfig={defaultersSortConfig} onSort={requestDefaultersSort} className="px-3 py-2 font-medium" />
                                                  <SortableHeader label="Offences" sortKey="count" sortConfig={defaultersSortConfig} onSort={requestDefaultersSort} className="px-3 py-2 text-center font-medium" />
                                                  <SortableHeader label="Total Marks" sortKey="marks" sortConfig={defaultersSortConfig} onSort={requestDefaultersSort} className="px-3 py-2 text-center font-medium" />
                                                  <SortableHeader label="Severity" sortKey="severity" accessor={r => r.marks} sortConfig={defaultersSortConfig} onSort={requestDefaultersSort} className="px-3 py-2 text-center font-medium" />
                                                </tr>
                                              </thead>
                                              <tbody className="divide-y divide-slate-100">
                                                {sortedDefaulters.map((row, i) => {
                                                  const severity = getSeverity(row.marks);
                                                  return (
                                                    <tr key={row.name} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                                      <td className="px-3 py-2 text-slate-500 font-mono">
                                                        {(defaultersPage - 1) * defaultersPageSize + i + 1}
                                                      </td>
                                                      <td className="px-3 py-2 font-medium text-slate-800">{row.name}</td>
                                                      <td className="px-3 py-2 text-center text-slate-600">{row.count}</td>
                                                      <td className="px-3 py-2 text-center font-bold text-red-600">{row.marks}</td>
                                                      <td className="px-3 py-2 text-center">
                                                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${severity.cls}`}>{severity.label}</span>
                                                      </td>
                                                    </tr>
                                                  );
                                                })}
                                              </tbody>
                                            </table>
                                          </div>
                                          <TablePager
                                            page={defaultersPage}
                                            setPage={setDefaultersPage}
                                            total={defaultersTotal}
                                            className="flex items-center justify-between px-3 py-2 border-t border-slate-100"
                                          />
                                          <div className="mt-2 border-t border-slate-100 pt-2 flex items-center gap-4 text-xs text-slate-500">
                                            <span><span className="inline-flex px-1.5 py-0.5 rounded-full bg-red-100 text-red-800 font-semibold">High</span> ≥ {severityThresholds.high} marks</span>
                                            <span><span className="inline-flex px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-800 font-semibold">Medium</span> {severityThresholds.medium}–{severityThresholds.high - 1} marks</span>
                                            <span><span className="inline-flex px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-800 font-semibold">Low</span> 1–{severityThresholds.medium - 1} marks</span>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                    <DataTable data={filteredTimesheets} category="timesheetDefaulters" />
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                            <p className="text-slate-500 text-center">No timesheet defaulters data available</p>
                        </div>
                    )}
                </div>
            )}
            {activeTab === 'chargeableHours' && (
                <div className="space-y-6 animate-fade-in">
                    <ChargeableHoursDetails
                      data={chargeableHoursData?.data || []}
                      summary={chargeableHoursData?.summary}
                      dateRange={chargeableMonthRange}
                      onDateRangeChange={setChargeableMonthRange}
                      loading={loadingChargeableHours}
                    />
                </div>
            )}
            {activeTab === 'headcount' && (
                <div className="space-y-6 animate-fade-in">
                    {loadingHeadcount ? (
                        <div className="flex justify-center items-center py-12">
                            <LoadingSpinner />
                        </div>
                    ) : (
                        <HeadcountDetails
                            data={headcountRows}
                            summary={{ headcountAnalysis: headcountAnalysis || {} }}
                        />
                    )}
                </div>
            )}
            {activeTab === 'complaints' && (
                <div className="space-y-6 animate-fade-in">
                    <Complaints />
                </div>
            )}
            {activeTab === 'unlocking' && (
                <div className="space-y-6 animate-fade-in">
                    <UnlockingArchived />
                </div>
            )}
            {activeTab === 'eqrLapses' && (
                <div className="space-y-6 animate-fade-in">
                    <EqrLapses />
                </div>
            )}
            {activeTab === 'cyberSecurity' && (
                <div className="space-y-6 animate-fade-in">
                    <CyberSecurity />
                </div>
            )}
            {activeTab === 'acLapses' && (
                <div className="space-y-6 animate-fade-in">
                    <AcLapses />
                </div>
            )}
        </main>
      </div>
    </div>
  );
};

export default QMDashboard;
